import { XIcon } from "@heroicons/react/outline";
import { sortBy } from "lodash";
import moment from "moment";
import { useEffect, useRef } from "react";
import { KpiSummaryPeriod } from "types/graphql-schema";

import KPILineChart from "@apps/kpis/components/kpi-line-chart";
import { KPISummaryTableCell } from "@apps/kpis/components/summary-table-row";
import { kpiSummaryPeriodOptions } from "@apps/kpis/helpers";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import AppLink from "@components/link/link";
import DeprecatedSelect from "@components/select/select";
import { classNames } from "@helpers/css";

import { viewOptions } from "./kpi-view-constants";
import { singleKpiViewLimit } from "./single-kpi-view";

const KpiView = ({
  kpi,
  deleteNode,
  selected,
  summaryPeriod,
  setSummaryPeriod,
  setViewMode,
  viewMode,
  isEmbeddedInKpiGroup = false,
  onAddValue = () => {},
}: {
  kpi: any;
  deleteNode?: () => void;
  selected: boolean;
  summaryPeriod?: KpiSummaryPeriod;
  setSummaryPeriod?: (sp: KpiSummaryPeriod) => void;
  setViewMode: (viewMode: string) => void;
  viewMode: string;
  isEmbeddedInKpiGroup: boolean;
  onAddValue?: () => void;
}) => {
  const measurements = sortBy(kpi.summarizedMeasurements.edges, ({ node }) =>
    moment(node.date).unix(),
  );
  const measurementCellClassName = "text-center px-2 w-36 flex-none";
  const measurementHeaderCellClassName = classNames(measurementCellClassName);
  const scrollContainerRef = useRef<null | HTMLDivElement>(null);

  const handleChangeSummaryPeriod = ({
    value,
  }: {
    value: KpiSummaryPeriod;
  }) => {
    if (setSummaryPeriod) setSummaryPeriod(value);
  };
  const handleChangeView = ({ value }: { value: string }) => setViewMode(value);

  const filteredMeasurements = sortBy(
    measurements,
    ({ node }) => -moment(node.date).unix(),
  )
    .slice(0, singleKpiViewLimit)
    .reverse();

  const hasDates = filteredMeasurements.length;

  useEffect(() => {
    if (hasDates && scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft =
        scrollContainerRef.current.scrollWidth;
    }
  }, [hasDates]);

  return (
    <div
      className={classNames(
        "flex flex-col gap-2 border border-gray-200 rounded-md bg-artifact-background not-prose",
        !isEmbeddedInKpiGroup &&
          selected &&
          "ring-2 ring-blue-200 ring-offset-2",
      )}
      contentEditable={false}
      aria-label="Artifact kpi view"
    >
      {kpi?.currentMeasurement ? (
        <div className="flex flex-col gap-2">
          {/* 12px horizontal / 10px vertical padding; title is gray-800 to
              match the other artifact embeds. Table/Graph picker is inline
              on the header row as a medium-sized button-dropdown. */}
          <div className="flex items-center justify-between gap-2 px-3 py-2.5">
            <div className="min-w-0 truncate">
              <AppLink
                to={`/kpis/${kpi.id}`}
                className="text-sm font-medium text-gray-800 hover:underline"
              >
                {kpi.title}
              </AppLink>
              <span className="text-xs font-normal text-gray-500 pl-2">
                (current value: {kpi.currentMeasurement.measurement})
              </span>
            </div>
            <div className="flex items-center gap-1">
              {viewMode && !isEmbeddedInKpiGroup && (
                <Dropdown
                  align="end"
                  size={ButtonSize.medium}
                  options={viewOptions.map((opt) => ({
                    label: opt.label,
                    onClick: () => handleChangeView(opt),
                  }))}
                >
                  <Button
                    size={ButtonSize.medium}
                    theme={buttonTheme.default}
                    showDownChevronIcon
                  >
                    {viewOptions.find((o) => o.value === viewMode)?.label ??
                      "Table"}
                  </Button>
                </Dropdown>
              )}
              {deleteNode && (
                <Button
                  size={ButtonSize.small}
                  theme={buttonTheme.iconGray}
                  icon={XIcon}
                  tooltip="Remove KPI"
                  onClick={deleteNode}
                />
              )}
            </div>
          </div>
          {summaryPeriod && !isEmbeddedInKpiGroup && (
            <div className="flex gap-2 px-3">
              <DeprecatedSelect
                onChange={handleChangeSummaryPeriod}
                value={summaryPeriod}
                options={kpiSummaryPeriodOptions}
              />
            </div>
          )}
          {viewMode === "table" && (
            <div
              className="overflow-x-auto overscroll-none"
              ref={scrollContainerRef}
            >
              <table className="rounded-b-lg w-full">
                <thead>
                  <tr>
                    {filteredMeasurements.map(({ node }, i) => (
                      <th
                        key={node.date}
                        className={classNames(
                          "border! border-gray-300! border-r-0! text-xs font-medium p-2 bg-gray-50 text-gray-600 tracking-tight text-center!",
                          measurementHeaderCellClassName,
                          i === 0 && "border-l-0!",
                        )}
                      >
                        {moment(node.date).format("ll")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {filteredMeasurements.map(({ node }, i) => (
                      <td
                        className={classNames(
                          "border! p-0! border-b-0! border-r-0!",
                          i === 0 && "border-l-0!",
                        )}
                        key={node.date}
                      >
                        <KPISummaryTableCell
                          key={i}
                          kpi={kpi}
                          isLastCell={i === filteredMeasurements.length - 1}
                          measurement={node}
                          previousMeasurement={
                            filteredMeasurements[i - 1]?.node
                          }
                          className={classNames(
                            "text-center px-2 h-14 py-2 text-gray-500 text-sm",
                          )}
                          onAddValue={onAddValue}
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {viewMode === "graph" && (
            <div>
              <KPILineChart kpi={kpi} height={200} />
            </div>
          )}
        </div>
      ) : (
        <div className="text-gray-500 text-sm px-2 py-1">No measurement.</div>
      )}
    </div>
  );
};

export default KpiView;
